import {BookingModel} from "./booking.model";
import {IServiceFactory} from "../../service-factory.interface";
import {AirlineWebapiErrorCodesEnum} from "../../airline-webapi/airline-webapi-error-codes.enum";
import {IBookingErrorHandlingViewModel} from "./booking-error-handling.view-model.interface";


export class BookingErrorHandlingModel implements IBookingErrorHandlingViewModel {
    constructor(private readonly booking: BookingModel) {
    }

    private get services(): IServiceFactory {
        return this.booking.services;
    }

    private async _refreshBookingData(): Promise<void> {
        const bookingData = await this.booking.session.bookingStateQueryBuilder().useBookingData().getBookingState();
        this.booking.updateBookingSessionData(bookingData);
    }

    canHandleApiError(apiErrorCode: AirlineWebapiErrorCodesEnum): boolean {
        return Boolean(this.getApiErrorMessage(apiErrorCode));
    }

    getGenericPaymentError(): string {
        return this.services.language.translate('There was an error processing your payment.');
    }

    getApiErrorMessage(apiErrorCode: AirlineWebapiErrorCodesEnum): string {
        switch (apiErrorCode) {
            case AirlineWebapiErrorCodesEnum.NskServerClassNotAvailable:
                return this.services.language.translate('Unfortunately this ticket was already taken by another customer. Please start over and choose a different flight');
            case AirlineWebapiErrorCodesEnum.NskServerBookingWasModified:
                return this.services.language.translate(`We're sorry! This booking was updated in another session and can't be modified in your current one. To make changes, you'll need to start from the beginning.`);
            case AirlineWebapiErrorCodesEnum.StandByInventoryExhausted:
                return this.services.language.translate(`We're sorry! All standby seats are sold out.`);
            case AirlineWebapiErrorCodesEnum.BotVerificationFailed:
                return this.services.language.translate(`We could not verify your browser. Please refresh the page and try again.`);
            default:
                return "";
        }
    }

    async handleApiError(apiErrorCode: AirlineWebapiErrorCodesEnum): Promise<void> {
        switch (apiErrorCode) {
            case AirlineWebapiErrorCodesEnum.NskServerClassNotAvailable:
                await this._handleNskServerClassNotAvailable();
                break;
            case AirlineWebapiErrorCodesEnum.NskServerBookingWasModified:
                await this._handleBookingWasModifiedError();
                break;
            case AirlineWebapiErrorCodesEnum.StandByInventoryExhausted:
                await this._handleStandbyInventoryExhausted();
                break;
            case AirlineWebapiErrorCodesEnum.BotVerificationFailed:
                await this._handleBotVerificationFailed();
                break;
            default:
                throw new Error(`Can't handle API error code ${apiErrorCode}`);
        }
    }

    private async _handleNskServerClassNotAvailable(): Promise<void> {
        await this.booking.bookingStrategyAdapter.goBackToFlightSelection();
        await this.services.loadingIndicator.execute({
            action: async () => {
                await this._refreshBookingData();
                await this.booking.flightSearchController.applySearch();
            }
        });

        this.services.alert.showError(this.getApiErrorMessage(AirlineWebapiErrorCodesEnum.NskServerClassNotAvailable));

    }

    private async _handleBookingWasModifiedError(): Promise<void> {
        await this.services.alert.showErrorAsync(this.getApiErrorMessage(AirlineWebapiErrorCodesEnum.NskServerBookingWasModified));
        await this.services.navigator.goHome({
            skipRoutingGuards: true
        });
    }

    private async _handleStandbyInventoryExhausted(): Promise<void> {
        await this.services.alert.showErrorAsync(this.getApiErrorMessage(AirlineWebapiErrorCodesEnum.StandByInventoryExhausted));
        await this.services.navigator.goHome({
            skipRoutingGuards: true
        });
    }

    //the user stays on the payment page so a refresh/retry can succeed
    private async _handleBotVerificationFailed(): Promise<void> {
        await this.services.alert.showErrorAsync(this.getApiErrorMessage(AirlineWebapiErrorCodesEnum.BotVerificationFailed));
    }
}